import { useState } from "react";
import Button from "../../components/Button";
import SallieReferral from "../questionnaire/components/SallieReferral";
import { useTranslation } from "../../i18n/TranslationProvider";
import { useAuth } from "../../contexts/AuthContext";
import AdminNavBar from "../admin/components/AdminNavBar";

const ADMIN_ROLES = ["Administrator", "Athletic Director/Administrator"];


const decisionTree = {
    headhit_Front_of_Head: {
        questionKey: "decisionTree.hitFrontOfHead",
        yes: "headhit_Back",
        no: "imm_symp_Incoherent_Speech_1",
    },
    imm_symp_Incoherent_Speech_1: {
        questionKey: "decisionTree.incoherentSpeech",
        yes: "headhit_Back_of_Head",
        no: "headhit_Back_left",
    },
    headhit_Back_left: {
        questionKey: "decisionTree.hitBackOfBody",
        yes: "imm_symp_Confusion_2",
        no: "imm_symp_Confusion_1",
    },
    imm_symp_Confusion_1: {
        questionKey: "decisionTree.confusion",
        yes: "end_0_278",
        no: "end_0_2251",
    },
    imm_symp_Confusion_2: {
        questionKey: "decisionTree.confusion",
        yes: "end_0_3286",
        no: "end_0_2668",
    },
    headhit_Back_of_Head: {
        questionKey: "decisionTree.hitBackOfHead",
        yes: "end_0_3614",
        no: "imm_symp_Dazed_or_Vacant_Stare",
    },
    imm_symp_Dazed_or_Vacant_Stare: {
        questionKey: "decisionTree.dazedOrVacantStare",
        yes: "end_0_3265",
        no: "end_0_2907",
    },
    headhit_Back: {
        questionKey: "decisionTree.hitBackOfBody",
        yes: "imm_symp_Incoherent_Speech_2",
        no: "imm_symp_Memory_Loss",
    },
    imm_symp_Memory_Loss: {
        questionKey: "decisionTree.memoryLoss",
        yes: "imm_symp_Incoherent_Speech_3",
        no: "end_0_2671",
    },
    imm_symp_Incoherent_Speech_3: {
        questionKey: "decisionTree.incoherentSpeech",
        yes: "end_0_3709",
        no: "end_0_3244",
    },
    imm_symp_Incoherent_Speech_2: {
        questionKey: "decisionTree.incoherentSpeech",
        yes: "headhit_Top_of_Head",
        no: "end_0_3952",
    },
    headhit_Top_of_Head: {
        questionKey: "decisionTree.hitTopOfHead",
        yes: "end_0_4674",
        no: "end_0_4197",
    },
    // End nodes (predictions)
    end_0_3614: { prediction: "0.3614", decision: "pull" },
    end_0_2671: { prediction: "0.2671", decision: "play" },
    end_0_278: { prediction: "0.278", decision: "play" },
    end_0_2251: { prediction: "0.2251", decision: "play" },
    end_0_3952: { prediction: "0.3952", decision: "pull" },
    end_0_4674: { prediction: "0.4674", decision: "pull" },
    end_0_4197: { prediction: "0.4197", decision: "pull" },
    end_0_2907: { prediction: "0.2907", decision: "play" },
    end_0_3265: { prediction: "0.3265", decision: "pull" },
    end_0_3709: { prediction: "0.3709", decision: "pull" },
    end_0_3244: { prediction: "0.3244", decision: "pull" },
    end_0_2668: { prediction: "0.2668", decision: "play" },
    end_0_3286: { prediction: "0.3286", decision: "pull" },
};

function useEMRFlow() {
    const [currentNode, setCurrentNode] = useState("headhit_Front_of_Head");
    const [answers, setAnswers] = useState({});
    const [score, setScore] = useState(null);
    const [decision, setDecision] = useState(null);
    

    const handleAnswer = (answer) => {
        const nextNode = decisionTree[currentNode][answer];
        const updated = { ...answers, [currentNode]: answer === "yes" };
        setAnswers(updated);

        if (nextNode.startsWith("end_")) {
            const { prediction, decision: dec } = decisionTree[nextNode];
            setScore(prediction);
            setDecision(dec);
        } else {
            setCurrentNode(nextNode);
        }
    };

    const restart = () => {
        setCurrentNode("headhit_Front_of_Head");
        setAnswers({});
        setScore(null);
        setDecision(null);
    };

    return { currentNode, answers, score, decision, handleAnswer, restart };
}

const EMRDashboard = () => {
    const { t } = useTranslation();
    const { currentNode, answers, score, decision, handleAnswer, restart } =
        useEMRFlow();
    const {userProfile} = useAuth();
    const isAdmin = ADMIN_ROLES.includes(userProfile?.user_type);

    const renderHistory = () =>
        Object.entries(answers).map(([key, val]) => (
            <div key={key} className="flex justify-between mb-2">
                <p>{t(decisionTree[key].questionKey)}</p>
                <p
                    className={`font-semibold ${
                        val ? "text-green-500" : "text-red-500"
                    }`}
                >
                    {val ? t("common.yes") : t("common.no")}
                </p>
            </div>
        ));

    return (
        <> 
             {isAdmin && (
                <AdminNavBar />
            )}
            <div
                className={`custom-container mx-auto w-4/5 md:w-2/3 lg:w-1/2  ${
                    isAdmin ? "!rounded-tl-none" : "rounded-t-lg"
                }`}
            >
                <h3 className="text-lg font-bold mb-6 text-center md:text-2xl">
                    {t("decisionFlow.title")}
                </h3>
                 <div className="my-6">{renderHistory()}</div>
                {score == null ? (
                    <>
                        <p className="mb-4 font-semibold text-base text-center md:text-lg">
                            Q. {t(decisionTree[currentNode].questionKey)}
                        </p>
                        <div className="flex gap-4 justify-center">
                            <Button onClick={() => handleAnswer("yes")}>
                                {t("common.yes")}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => handleAnswer("no")}
                            >
                                {t("common.no")}
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="flex flex-col items-center text-center gap-1">
                            <p className="text-base font-semibold md:text-lg">
                                {t("decisionFlow.recommendationLabel")}
                            </p>
                            <p
                                className={`font-bold text-lg md:text-2xl ${
                                    decision === "pull"
                                        ? "text-red-600"
                                        : "text-green-600"
                                }`}
                            >
                                {decision === "pull"
                                    ? t("emr.referNeeded")
                                    : t("emr.noReferralNeeded")}
                            </p>
                        </div>
                        <div
                            className={`
                                flex items-center justify-center mx-auto w-32 h-32 mt-6 rounded-full border text-3xl text-white font-bold mb-6
                                ${decision === "pull" ? "bg-red-500" : "bg-green-500"}`}
                        >
                            {score}
                        </div>
                        <div className="space-y-4 text-center flex flex-col items-center">
                            <SallieReferral />
                        </div>
                    </>
                )}
                <hr className="border-t border-gray-300 my-6" />
                <Button
                    onClick={restart}
                    className="w-full"
                >
                    {t("decisionFlow.restart")}
                </Button>
            </div>
        </>
    );
};

export default EMRDashboard;